import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ChevronLeft, ChevronRight, Loader2, CalendarDays, ShieldCheck, ArrowRight, ArrowLeft } from "lucide-react";
import { useDemoModal } from "../context/DemoModalContext";

const EASE = [0.16, 1, 0.3, 1];
const NAVY = "#0A234A";
// Same-origin endpoint: hits the Cloudflare Pages Function in production and
// the FastAPI backend in the Emergent preview (both serve /api/demo/book).
const BOOK_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/demo/book`;

const ROLES = [
  { label: "Driver", value: "Driver" },
  { label: "Dispatcher", value: "Dispatcher" },
  { label: "Owner-Operator", value: "Owner-Operator" },
  { label: "Depot", value: "Depot" },
  { label: "Gov't Agency", value: "Government" },
];
const FLEET = ["1–10", "11–50", "51–200", "200+"];
const TIMES = Array.from({ length: 16 }, (_, i) => {
  const h = 9 + Math.floor(i / 2);
  const m = i % 2 ? "30" : "00";
  return `${String(h).padStart(2, "0")}:${m}`;
});
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtTime = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)} ${ampm}`;
};

const MiniCalendar = ({ value, onSelect }) => {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const firstDow = view.getDay();
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const atCurrentMonth = view.getFullYear() === today.getFullYear() && view.getMonth() === today.getMonth();
  const shift = (delta) => setView(new Date(view.getFullYear(), view.getMonth() + delta, 1));

  return (
    <div data-testid="demo-calendar">
      <div className="mb-3 flex items-center justify-between">
        <button type="button" onClick={() => shift(-1)} disabled={atCurrentMonth} data-testid="cal-prev"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100 disabled:opacity-30">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-700" style={{ color: NAVY }}>{MONTHS[view.getMonth()]} {view.getFullYear()}</span>
        <button type="button" onClick={() => shift(1)} data-testid="cal-next"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, i) => <div key={i} className="py-1 text-[11px] font-600 text-neutral-400">{w}</div>)}
        {cells.map((day, i) => {
          if (day === null) return <div key={i} />;
          const d = new Date(view.getFullYear(), view.getMonth(), day);
          const iso = fmtDate(d);
          const disabled = d.getDay() === 0 || d.getDay() === 6 || d < today;
          const selected = value === iso;
          return (
            <button key={i} type="button" disabled={disabled}
              data-testid={selected ? "cal-day-selected" : undefined}
              onClick={() => onSelect(iso)}
              className={`aspect-square rounded-lg text-[13px] font-600 transition-all duration-200 ${
                selected ? "bg-[#FF5722] text-white shadow-[0_6px_14px_-6px_rgba(255,87,34,0.6)]"
                : disabled ? "text-neutral-300" : "text-neutral-700 hover:bg-[#FF5722]/10 hover:text-[#FF5722]"
              }`}>
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-[13px] font-600" style={{ color: NAVY }}>{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-sm text-neutral-800 outline-none transition-all duration-200 placeholder:text-neutral-400 focus:border-[#FF5722] focus:ring-2 focus:ring-[#FF5722]/20";

const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const STEP_LABELS = ["Your details", "Pick a time"];

export const DemoModal = () => {
  const { open, closeDemo } = useDemoModal();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", fleet_size: "", date: "", time: "", notes: "" });
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const step1Valid = form.name && emailOk(form.email) && form.company && form.role;
  const step2Valid = form.date && form.time;
  const isDone = status === "success";
  const progress = isDone ? 100 : step === 1 ? 50 : 100;

  const reset = () => {
    setStep(1);
    setForm({ name: "", email: "", company: "", role: "", fleet_size: "", date: "", time: "", notes: "" });
    setStatus("idle"); setErrMsg(""); setResult(null);
  };
  const handleClose = () => { closeDemo(); setTimeout(reset, 300); };

  const submit = async (e) => {
    e.preventDefault();
    if (!step2Valid || status === "loading") return;
    setStatus("loading"); setErrMsg("");
    try {
      const res = await fetch(BOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, fleet_size: form.fleet_size || null, notes: form.notes || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
      setResult(data); setStatus("success");
    } catch (err) {
      setErrMsg(err.message || "Something went wrong."); setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="demo-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div className="absolute inset-0 bg-[#071429]/60 backdrop-blur-sm" onClick={handleClose}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />

          <motion.div
            className="relative z-10 grid max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-[24px] bg-white shadow-[0_40px_100px_-30px_rgba(7,20,41,0.7)] lg:grid-cols-[0.85fr_1.15fr]"
            initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }} transition={{ duration: 0.4, ease: EASE }}
          >
            {/* progress bar (orange) — top of modal */}
            <div className="absolute left-0 top-0 z-20 h-1 w-full bg-black/5">
              <motion.div data-testid="demo-progress" className="h-full bg-[#FF5722]"
                initial={false} animate={{ width: `${progress}%` }} transition={{ duration: 0.4, ease: EASE }} />
            </div>

            {/* brand panel */}
            <div className="relative hidden flex-col justify-between p-8 pt-9 lg:flex" style={{ background: NAVY }}>
              <div>
                <p className="text-xs font-700 uppercase tracking-[0.16em] text-[#FF7A50]">Request a Demo</p>
                <h3 className="mt-3 font-display text-2xl font-700 leading-tight text-white">See FleetPods on your routes.</h3>
                <p className="mt-3 text-sm font-400 leading-relaxed text-white/60">
                  Book a 30-minute walkthrough of the app and hub network, tailored to your fleet.
                </p>
              </div>
              {/* step indicators */}
              <ul className="space-y-3">
                {STEP_LABELS.map((label, idx) => {
                  const n = idx + 1;
                  const active = !isDone && step === n;
                  const complete = isDone || step > n;
                  return (
                    <li key={label} className="flex items-center gap-3">
                      <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[13px] font-700 transition-colors ${
                        complete ? "bg-[#FF5722] text-white" : active ? "bg-white text-[#0A234A]" : "bg-white/10 text-white/50"
                      }`}>
                        {complete ? <Check className="h-4 w-4" strokeWidth={3} /> : n}
                      </span>
                      <span className={`text-sm font-600 ${active || complete ? "text-white" : "text-white/45"}`}>{label}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="flex items-center gap-2 text-xs font-500 text-white/40">
                <ShieldCheck className="h-4 w-4" /> Mon–Fri · 9am–5pm Eastern (Nunavut)
              </p>
            </div>

            {/* right side */}
            <div className="relative max-h-[90vh] overflow-y-auto p-6 pt-9 sm:p-8 sm:pt-9">
              <button type="button" onClick={handleClose} data-testid="demo-close"
                className="absolute right-4 top-5 flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700">
                <X className="h-5 w-5" />
              </button>

              {isDone ? (
                <div data-testid="demo-success" className="flex min-h-[420px] flex-col items-center justify-center text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FF5722]/10">
                    <Check className="h-8 w-8 text-[#FF5722]" strokeWidth={2.5} />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-700" style={{ color: NAVY }}>Request received!</h3>
                  <p className="mt-2 max-w-sm text-sm font-400 leading-relaxed text-neutral-500">
                    We&apos;ve emailed a confirmation to <strong>{form.email}</strong>. Your requested slot:
                  </p>
                  <p className="mt-3 rounded-xl bg-[#F6F7FA] px-4 py-2 text-sm font-600" style={{ color: NAVY }}>{result?.when}</p>
                  <button type="button" onClick={handleClose} data-testid="demo-done"
                    className="mt-7 rounded-xl bg-[#FF5722] px-6 py-3 text-sm font-600 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#F4511E]">
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <p className="mb-1 text-xs font-700 uppercase tracking-[0.16em] text-[#FF5722]">
                    Step {step} of 2 · {STEP_LABELS[step - 1]}
                  </p>

                  <AnimatePresence mode="wait">
                    {step === 1 ? (
                      <motion.div key="s1" data-testid="demo-step1"
                        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3, ease: EASE }} className="space-y-4 pt-2">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Full name">
                            <input data-testid="demo-name" className={inputCls} value={form.name}
                              onChange={(e) => set("name", e.target.value)} placeholder="Jordan Rivera" />
                          </Field>
                          <Field label="Work email">
                            <input data-testid="demo-email" type="email" className={inputCls} value={form.email}
                              onChange={(e) => set("email", e.target.value)} placeholder="jordan@company.com" />
                          </Field>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Company">
                            <input data-testid="demo-company" className={inputCls} value={form.company}
                              onChange={(e) => set("company", e.target.value)} placeholder="ACME Freight" />
                          </Field>
                          <Field label="Fleet size (optional)">
                            <select data-testid="demo-fleet" className={inputCls} value={form.fleet_size}
                              onChange={(e) => set("fleet_size", e.target.value)}>
                              <option value="">Select…</option>
                              {FLEET.map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                          </Field>
                        </div>
                        <div>
                          <span className="mb-1.5 block text-[13px] font-600" style={{ color: NAVY }}>I&apos;m a…</span>
                          <div className="flex flex-wrap gap-2">
                            {ROLES.map((r) => (
                              <button key={r.value} type="button" data-testid={`demo-role-${r.value}`}
                                onClick={() => set("role", r.value)}
                                className={`rounded-xl border px-3.5 py-2 text-[13px] font-600 transition-all duration-200 ${
                                  form.role === r.value ? "border-[#FF5722] bg-[#FF5722] text-white"
                                    : "border-black/10 bg-white text-neutral-600 hover:border-[#FF5722]/40"
                                }`}>
                                {r.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <Field label="Notes (optional)">
                          <textarea data-testid="demo-notes" rows={2} className={inputCls} value={form.notes}
                            onChange={(e) => set("notes", e.target.value)} placeholder="Routes, timelines, anything specific…" />
                        </Field>

                        <button type="button" data-testid="demo-continue" disabled={!step1Valid}
                          onClick={() => step1Valid && setStep(2)}
                          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF5722] px-6 py-3.5 text-[15px] font-600 text-white shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(160,40,0,0.35)] transition-all duration-300 hover:bg-[#F4511E] disabled:cursor-not-allowed disabled:opacity-50">
                          Continue <ArrowRight className="h-4 w-4" />
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div key="s2" data-testid="demo-step2"
                        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3, ease: EASE }} className="pt-2">
                        <div className="grid gap-5 rounded-2xl border border-black/5 bg-[#FAFBFC] p-4 sm:grid-cols-2">
                          <div>
                            <span className="mb-2 flex items-center gap-1.5 text-[13px] font-600" style={{ color: NAVY }}>
                              <CalendarDays className="h-4 w-4 text-[#FF5722]" /> Pick a date
                            </span>
                            <MiniCalendar value={form.date} onSelect={(d) => { set("date", d); set("time", ""); }} />
                          </div>
                          <div>
                            <span className="mb-2 block text-[13px] font-600" style={{ color: NAVY }}>Pick a time (ET)</span>
                            {form.date ? (
                              <div className="grid max-h-[240px] grid-cols-3 gap-1.5 overflow-y-auto pr-1">
                                {TIMES.map((t) => (
                                  <button key={t} type="button" data-testid={`demo-time-${t}`} onClick={() => set("time", t)}
                                    className={`rounded-lg border px-1 py-2 text-[12px] font-600 transition-all duration-200 ${
                                      form.time === t ? "border-[#FF5722] bg-[#FF5722] text-white"
                                        : "border-black/10 bg-white text-neutral-600 hover:border-[#FF5722]/40"
                                    }`}>
                                    {fmtTime(t)}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className="mt-2 text-[13px] font-400 text-neutral-400">Select a date first.</p>
                            )}
                          </div>
                        </div>

                        {status === "error" && (
                          <p data-testid="demo-error" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-500 text-red-600">{errMsg}</p>
                        )}

                        <div className="mt-5 flex items-center gap-3">
                          <button type="button" data-testid="demo-back" onClick={() => setStep(1)}
                            className="flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-5 py-3.5 text-[15px] font-600 text-neutral-600 transition-all duration-200 hover:border-[#FF5722]/40 hover:text-neutral-900">
                            <ArrowLeft className="h-4 w-4" /> Back
                          </button>
                          <button type="submit" data-testid="demo-submit" disabled={!step2Valid || status === "loading"}
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#FF5722] px-6 py-3.5 text-[15px] font-600 text-white shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(160,40,0,0.35)] transition-all duration-300 hover:bg-[#F4511E] disabled:cursor-not-allowed disabled:opacity-50">
                            {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Booking…</> : "Request my demo"}
                          </button>
                        </div>
                        <p className="mt-3 text-center text-[12px] font-400 text-neutral-400">
                          We&apos;ll confirm by email. Mon–Fri, 9am–5pm Eastern (Nunavut).
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
